// src/pages/Ecommerce.tsx
import React, { useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import {
    Container,
    Typography,
    Box,
    Card,
    CardContent,
    Divider,
    Button,
    Checkbox,
    FormControlLabel,
    Slider,
    Chip,
    Collapse,
    useTheme,
    useMediaQuery
} from '@mui/material';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import EcoIcon from '@mui/icons-material/CreditScore';
import SpaIcon from '@mui/icons-material/Compost';
import ToggleCard from '../components/ToggleCard';
import mapImage from '../assets/map.png';

type Item = { name: string; price: number; qty: number };

const Ecommerce: React.FC = () => {
    const theme = useTheme();
    const isDesktop = useMediaQuery(theme.breakpoints.up('sm'));
    const [completed, setCompleted] = useState(false);
    const [donationPct, setDonationPct] = useState(5);
    const confirmRef = useRef<HTMLDivElement>(null);

    const items: Item[] = [
        { name: 'Organic Cotton T-Shirt', price: 29.99, qty: 1 },
        { name: 'Bamboo Water Bottle', price: 24.99, qty: 1 },
        { name: 'Recycled Nylon Backpack', price: 79.99, qty: 1 },
    ];
    const shipping = 4.99;
    const subtotal = items.reduce((sum, i) => sum + i.price * i.qty, 0);
    const donation = parseFloat((subtotal * (donationPct / 100)).toFixed(2));
    const total = parseFloat((subtotal + shipping + donation).toFixed(2));

    const rightData = {
        mangroves: 14,
        carbon: 150,
        unit: 'kgCO2e',
        description:
            'Your purchase is directly funding ecosystem restoration in Indonesia, where local communities plant and protect mangrove forests.',
        location: 'Indonesia Mangrove Project',
        status: 'Active',
        equivalent: {
          value: 16,
          description: 'days of using aircon'
        },
        stats: [
            { icon: '🐠', text: 'Creates habitat for 30+ fish species' },
            { icon: '👨‍🌾', text: 'Supports 3 local communities' }
        ] as Array<{ icon: string; text: string }>
    };

    // create a serializable copy for ToggleCard
    const serializableRightData = {
        mangroves: rightData.mangroves,
        carbon: rightData.carbon,
        unit: rightData.unit,
        description: rightData.description,
        equivalent: rightData.equivalent,
        location: rightData.location,
        status: rightData.status,
        stats: rightData.stats.map(s => ({ icon: s.icon, text: s.text }))
    };

    const handleComplete = () => setCompleted(true);

    return (
        <Container maxWidth="lg">
            <Typography variant="h3" sx={{ color: 'rgb(79,111,82)', mb: 1 }}>
                Regenerative Shopping
            </Typography>
            <Typography variant="subtitle1" sx={{ mb: 4 }}>
                Every purchase directly funds ecosystem restoration with our API
            </Typography>

            <Box display="flex" flexDirection={isDesktop ? 'row' : 'column'} gap={4}>
                {/* Left: Checkout */}
                <Box flex={1}>
                    <Card>
                        <CardContent>
                            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                                <Typography
                                    variant="h5"
                                    sx={{ display: 'flex', alignItems: 'center', fontWeight: 700 }}
                                >
                                    <ShoppingCartIcon sx={{ mr: 1, color: 'rgb(101,183,65)' }} />
                                    Checkout
                                </Typography>
                            </Box>

                            {/* Items */}
                            {items.map((it, i) => (
                                <Box
                                    key={i}
                                    display="flex"
                                    justifyContent="space-between"
                                    alignItems="center"
                                    mb={i < items.length - 1 ? 1.5 : 3}
                                >
                                    <Box display="flex" alignItems="center" gap={1}>
                                        <ShoppingCartIcon color="disabled" />
                                        <Typography>{it.name}</Typography>
                                        <Chip label="Eco" size="small" sx={{ ml: 1, backgroundColor: 'rgb(239,253,219)' }} />
                                        <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                                            Qty: {it.qty}
                                        </Typography>
                                    </Box>
                                    <Typography fontWeight={700}>${it.price.toFixed(2)}</Typography>
                                </Box>
                            ))}

                            <Divider sx={{ mb: 2 }} />

                            {/* Subtotals */}
                            {[
                                ['Subtotal', subtotal],
                                ['Shipping', shipping],
                            ].map(([label, amt], i) => (
                                <Box
                                    key={i}
                                    display="flex"
                                    justifyContent="space-between"
                                    mb={i === 1 ? 3 : 1}
                                >
                                    <Typography>{label}</Typography>
                                    <Typography>${(amt as number).toFixed(2)}</Typography>
                                </Box>
                            ))}

                            {/* Contribution */}
                            <Box sx={{ backgroundColor: 'rgb(239,253,219)', p: 2, borderRadius: 1, mb: 3 }}>
                                <FormControlLabel
                                    disabled={true}
                                    control={<Checkbox defaultChecked />}
                                    label={
                                        <Typography sx={{ display: 'flex', alignItems: 'center', fontWeight: 700 }}>
                                            Add regenerative contribution 🌱
                                        </Typography>
                                    }
                                />
                                <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                                    <Typography>Percentage of order total</Typography>
                                    <Typography fontWeight={700}>{donationPct}%</Typography>
                                </Box>
                                <Slider
                                    value={donationPct}
                                    onChange={(_, v) => setDonationPct(v as number)}
                                    min={0}
                                    max={20}
                                    disabled={completed}
                                />
                                <Typography variant="body2" color="text.secondary" mt={1}>
                                    100% of your contribution directly funds mangrove restoration in Indonesia
                                </Typography>
                                <Box display="flex" justifyContent="space-between" mt={1}>
                                    <Typography fontWeight={700}>${donation.toFixed(2)}</Typography>
                                </Box>
                            </Box>

                            {/* Total + CTA */}
                            <Box display="flex" justifyContent="space-between" mb={2}>
                                <Typography variant="h6">Total</Typography>
                                <Typography variant="h6" fontWeight={700}>
                                    ${total.toFixed(2)}
                                </Typography>
                            </Box>
                            <Button
                                variant="contained"
                                fullWidth
                                sx={{ py: 1.5, fontWeight: 700 }}
                                onClick={handleComplete}
                                disabled={completed}
                            >
                                Complete Checkout
                            </Button>
                        </CardContent>
                    </Card>
                </Box>

                {/* Right: Impact */}
                <Box flex={1} ref={confirmRef} sx={{ scrollMarginTop: '80px' }}>
                    <Collapse
                        in={completed}
                        timeout={600}
                        unmountOnExit
                        onEntered={() =>
                            confirmRef.current?.scrollIntoView({ behavior: 'smooth' })
                        }
                    >
                        <Card>
                            <CardContent>
                                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                                    <Typography
                                        variant="h5"
                                        sx={{ display: 'flex', alignItems: 'center', fontWeight: 700, color: 'rgb(79,111,82)' }}
                                    >
                                        <EcoIcon sx={{ mr: 1, color: 'rgb(101,183,65)' }} />
                                        Order confirmed
                                    </Typography>

                                </Box>

                                <Typography variant="subtitle1" sx={{ mb: 2 }}>
                                    See how your purchase creates a positive impact.
                                </Typography>
                                <ToggleCard title="&nbsp;" code={serializableRightData} sx={{ border: '0px' }}>


                                    {/* Contribution */}
                                    <Box
                                      sx={{
                                        backgroundColor: 'rgb(239,253,219)',
                                        p: 3,
                                        borderRadius: 1,
                                        mb: 3,
                                        textAlign: 'center'
                                      }}
                                    >
                                      {/* Two-column layout */}
                                      <Box
                                        sx={{
                                          display: 'flex',
                                          justifyContent: 'space-around',
                                          mb: 2, // space between columns and the text below
                                        }}
                                      >
                                        <Box>
                                          <Typography variant="h4" fontWeight={700}>🌱</Typography>
                                          <Typography variant="h4" fontWeight={700}>{rightData.mangroves}</Typography>
                                          <Typography>Mangroves restored</Typography>
                                        </Box>
                                        <Box>
                                          <SpaIcon sx={{ fontSize: 40, color: 'rgb(101,183,65)' }} />
                                          <Typography variant="h4" fontWeight={700}>
                                            {rightData.carbon} {rightData.unit}
                                          </Typography>
                                          <Typography>absorbed per year</Typography>
                                        </Box>
                                      </Box>

                                      {/* Text below the columns */}
                                      <Typography fontWeight={400}>
                                        This is equivalent to {rightData.equivalent.value} {rightData.equivalent.description}
                                      </Typography>
                                    </Box>


                                    <Typography variant="body2" mb={3}>
                                        {rightData.description}
                                    </Typography>

                                    {/* Location Impact */}
                                    <Typography variant="subtitle1" fontWeight={700} mb={1}>
                                        Location Impact
                                    </Typography>
                                    <Card variant="outlined" sx={{ mb: 3 }}>
                                        <CardContent>
                                            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                                                <Typography fontWeight={700}>{rightData.location}</Typography>
                                                <Chip
                                                    label={rightData.status}
                                                    size="small"
                                                    sx={{ backgroundColor: 'rgb(239,253,219)' }}
                                                />
                                            </Box>
                                            <Box
                                                component="img"
                                                src={mapImage}
                                                alt="Project map"
                                                sx={{
                                                    height: 160,
                                                    width: '100%',
                                                    objectFit: 'cover',
                                                    borderRadius: 1,
                                                    mb: 2,
                                                }}
                                            />
                                            <Box display="flex" gap={2}>
                                                {rightData.stats.map((s, i) => (
                                                    <Box key={i} display="flex" alignItems="center" gap={1}>
                                                        <Typography fontSize="1.25rem">{s.icon}</Typography>
                                                        <Typography variant="body2">{s.text}</Typography>
                                                    </Box>
                                                ))}
                                            </Box>
                                        </CardContent>
                                    </Card>

                                    {/* Tracking CTA */}
                                    <Box
                                        component={Link}
                                        to="/engagement"
                                        sx={{
                                            textDecoration: 'none',     // remove underline
                                            color: 'inherit',           // inherit text colour
                                            backgroundColor: 'rgb(239,253,219)',
                                            p: 2,
                                            borderRadius: 1,
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            alignItems: 'center',
                                            cursor: 'pointer',
                                            '&:hover': {
                                                backgroundColor: 'rgb(227,244,204)',
                                                textDecoration: 'none'    // ensure no underline on hover
                                            }
                                        }}
                                    >
                                        <Typography fontWeight={700}>Track Your Impact <span style={{ fontWeight: 400 }}>- get exciting updates and stories!</span></Typography>
                                        <Typography>{'→'}</Typography>
                                    </Box>
                                </ToggleCard>
                            </CardContent>
                        </Card>
                    </Collapse>
                </Box>
            </Box>
        </Container>
    );
};

export default Ecommerce;
